import React, {useContext, useEffect, useRef, useState} from "react";
import {toast} from "react-toastify";
import {
    Autocomplete,
    Box,
    Checkbox,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    FormHelperText,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import {menuItemsInfo} from "../editMenuItems";
import {HugeiconsIcon} from "@hugeicons/react";
import {Add01Icon, Cancel01Icon, Clock01Icon, FloppyDiskIcon, Maximize01Icon} from "@hugeicons/core-free-icons";
import MarkdownEditorModal from "../../../../../components/modals/MarkdownEditorModal";
import {SystemPromptHistoryModal} from "./SystemPromptHistoryModal";
import {CopyCodeButton} from "../../../../../components/CopyCodeButton";
import {selectNodeModel, useLlmModels} from "../../../../../context/CatalogContext";
import {SignInContext} from "../../../../../context/SignInContext";
import {TopicRepository} from "../../../../../repositories/TopicRepository";
import {NodeType} from "../../../../../model/ModelData";
import {Context, getMcpScopeKey} from "../../../../../context/AppContext";
import {useRegistryProject} from "../../../../registry-project/RegistryProjectContext";
import {UI, rgba} from "../../uiTokens";
import {
    fieldSx,
    iconBtnDangerSx,
    iconBtnSx,
    settingsTableHeadCellSx as thSx,
    settingsTableSx as tableSx
} from "../editMenuStyles";
import {SectionHeader, SliderRow, SubSectionHeader} from "../components/EditMenuSection";
import {SettingsCustomParams} from "../components/SettingsCustomParams";
import {REASONING_EFFORT_KEY, ReasoningEffortField} from "../components/ReasoningEffortField";

export const LLMConfigurationTab = ({node, setNode, onUpdate, project, flow}) => {
    const variables = project?.flow_variables?.[flow?.id] || [];
    const [expandPersona, setExpandPersona] = useState(false);
    const [expandUserInputMessage, setExpandUserInputMessage] = useState(false);
    const [showPromptHistory, setShowPromptHistory] = useState(false);

    const {models, modelIds} = useLlmModels();
    const {state, getAllServersWithDetails} = useContext(Context);
    const {currentUser} = useContext(SignInContext);
    const {mcpServers: allMcpServers, mcpScopeKey: loadedMcpScopeKey, mcpLoading, kbTopics} = state;

    const {project: registryProject, subProject} = useRegistryProject();
    const [topics, setTopics] = useState([]);
    const [selectedOrganization, setSelectedOrganization] = useState(null);

    const isKbNode = [NodeType.GEN_AI_WORKFLOW, NodeType.CHATBOT, NodeType.AGENT, NodeType.QA].includes(node?.type);

    useEffect(() => {
        setSelectedOrganization(project?.settings?.organization || null);
        if (isKbNode) return;
        TopicRepository.getAll().then(response => setTopics(response.data)).catch(err => console.log(err));
    }, [node?.type, project?.settings?.organization]);

    const activeTopics = isKbNode ? kbTopics : topics;

    useEffect(() => {
        if (!isKbNode || !node?.data?.topic_id) return;
        const selectedTopic = activeTopics?.find(t => String(t?.id) === String(node.data.topic_id));
        const isInactive = selectedTopic?.server?.status && selectedTopic.server.status !== "online";
        if (isInactive) {
            toast.warning(`Topic "${selectedTopic.name}" server is ${selectedTopic.server.status} — may fail at runtime.`);
        }
    }, [node?.data?.topic_id, activeTopics]);

    // registry projects keep the MCP registry on their backend configuration, legacy flows on their own configuration
    const mcpEnabled = registryProject
        ? !!registryProject.backendConfiguration?.mcpRegistry
        : !!project?.configuration?.mcp_registry;

    // registry sub projects must use the same full scope as RegistryDesignerPage, or the narrower result overwrites it
    const mcpScope = registryProject
        ? {flowUid: project?.id, projectId: registryProject.id, subProjectId: subProject?.id}
        : project?.id;
    const mcpScopeKey = getMcpScopeKey(mcpScope);
    // the shared list is only trusted for the scope it was loaded for
    const mcpListMatches = loadedMcpScopeKey === mcpScopeKey;
    const mcpServers = mcpListMatches ? allMcpServers : [];
    const requestedMcpScope = useRef(null);

    useEffect(() => {
        if (!project?.id || !mcpEnabled) return;
        if (mcpListMatches || mcpLoading) return;
        // the editor page normally loads the list; fetch here only if it did not, and once per scope
        if (requestedMcpScope.current === mcpScopeKey) return;
        requestedMcpScope.current = mcpScopeKey;
        getAllServersWithDetails(mcpScope);
    }, [project?.id, mcpEnabled, mcpListMatches, mcpLoading, mcpScopeKey]);

    const mcpReady = !mcpEnabled || mcpListMatches;

    const allTools = [
        ...(project?.tools || []).map(tool => ({
            name: tool?.function?.name,
            description: tool?.function?.description,
            serverName: "Custom Tools",
            serverId: null,
            serverActive: true,
            source: "custom",
            raw: tool,
        })),
        ...(mcpServers || []).flatMap(server => (server.tools || []).map(tool => ({
            name: tool.name,
            description: tool.description,
            serverName: server.name,
            serverId: server.id,
            serverActive: server.active,
            source: "mcp",
            raw: tool,
        }))),
    ];

    const toolsEqual = (a = [], b = []) => {
        const normalize = (arr) => JSON.stringify([...arr].map(t => (typeof t === "string" ? t : t.id ?? t.name)).sort());
        return normalize(a) === normalize(b);
    };

    useEffect(() => {
        const attached = node?.data?.skills;
        if (!attached?.length || !project?.skills) return;

        let changed = false;
        const synced = [];
        for (const s of attached) {
            const fresh = project.skills.find(ps => ps.id === s.id);
            if (!fresh) {
                changed = true;
                continue;
            }
            if (
                fresh.content !== s.content ||
                fresh.name !== s.name ||
                fresh.description !== s.description ||
                !toolsEqual(fresh.tools, s.tools)
            ) {
                changed = true;
                synced.push({
                    id: s.id,
                    name: fresh.name,
                    description: fresh.description || "",
                    content: fresh.content || "",
                    tools: fresh.tools || [],
                });
            } else {
                synced.push(s);
            }
        }

        if (changed) {
            onUpdate({...node, data: {...node.data, skills: synced}});
        }
    }, [project?.skills, node?.id]);

    const onChangeData = (key, value) => {
        setNode(prev => {
            const state = {...prev};
            state.data[key] = value;
            return state;
        });
    };

    const MAX_SYSTEM_PROMPT_VERSIONS = 3;

    // Checkpoints the current system prompt into history (newest first, capped), separate from the
    // regular autosave-on-blur so history only grows when explicitly saved, not on every keystroke.
    const saveSystemPromptVersion = () => {
        const value = typeof node?.data?.system_prompt === "string" ? node.data.system_prompt : "";
        const history = node?.data?.system_prompt_history || [];
        if (value === history[0]?.value) return;
        const entry = {value, savedAt: new Date().getTime(), savedBy: currentUser?.username || null};
        const updatedNode = {
            ...node,
            data: {...node.data, system_prompt_history: [entry, ...history].slice(0, MAX_SYSTEM_PROMPT_VERSIONS)},
        };
        setNode(updatedNode);
        onUpdate(updatedNode);
    };

    const restoreSystemPrompt = (value) => {
        const updatedNode = {...node, data: {...node.data, system_prompt: value}};
        setNode(updatedNode);
        onUpdate(updatedNode);
        setShowPromptHistory(false);
    };

    const {primary, secondary} = menuItemsInfo.llm_configuration;

    return (
        <>
            <MarkdownEditorModal
                open={expandPersona}
                onClose={() => {
                    setExpandPersona(false);
                    onUpdate(node);
                }}
                title="System Prompt"
                variables={variables}
                value={typeof node?.data?.system_prompt === "string" ? node.data.system_prompt : ""}
                onChange={(e) => onChangeData("system_prompt", e.target.value)}
            />
            <MarkdownEditorModal
                open={expandUserInputMessage}
                onClose={() => {
                    setExpandUserInputMessage(false);
                    onUpdate(node);
                }}
                title="User Input"
                value={typeof node?.data?.user_message === "string" ? node.data.user_message : ""}
                onChange={(e) => onChangeData("user_message", e.target.value)}
            />
            <SystemPromptHistoryModal
                open={showPromptHistory}
                onClose={() => setShowPromptHistory(false)}
                history={node?.data?.system_prompt_history || []}
                currentValue={typeof node?.data?.system_prompt === "string" ? node.data.system_prompt : ""}
                onRestore={restoreSystemPrompt}
            />

            <SectionHeader title={primary} description={secondary}/>

            <SubSectionHeader title="AI Properties"/>
            <FormControl size="small" sx={fieldSx}>
                <InputLabel id="llm-config-model-label">Model</InputLabel>
                <Select
                    labelId="llm-config-model-label"
                    value={node?.data?.model || "none"}
                    label="Model"
                    onChange={e => selectNodeModel(onChangeData, models, e.target.value === "none" ? null : e.target.value)}
                    onBlur={() => onUpdate(node)}
                >
                    {modelIds?.map(model => (
                        <MenuItem key={model} value={model}>{model}</MenuItem>
                    ))}
                </Select>
            </FormControl>
            <ReasoningEffortField node={node} setNode={setNode} onUpdate={onUpdate}/>

            <SliderRow
                label="Temperature"
                display={node?.data?.temp?.toFixed(2) || 0}
                value={node?.data?.temp || 0}
                min={0} max={1} step={0.05}
                onChange={e => onChangeData("temp", e.target.value)}
                onBlur={() => onUpdate(node)}
            />
            <SliderRow
                label="MAX Output Tokens"
                display={node?.data?.max_output_tokens || 0}
                value={node?.data?.max_output_tokens || 0}
                min={0} max={1024} step={1}
                onChange={e => onChangeData("max_output_tokens", e.target.value)}
                onBlur={() => onUpdate(node)}
            />

            <Box>
                <SubSectionHeader title="GenAI Goal"/>
                <TextField
                    value={node?.data?.genai_goal}
                    size="small"
                    multiline
                    maxRows={3}
                    placeholder="GenAI Goal"
                    fullWidth
                    sx={{mt: 0.75, ...fieldSx}}
                    onChange={(e) => onChangeData("genai_goal", e.target.value)}
                    onBlur={() => onUpdate(node)}
                />
            </Box>

            <Box>
                <SubSectionHeader
                    title="System Prompt"
                    action={
                        <Stack direction="row" gap={0.5}>
                            <CopyCodeButton
                                text={typeof node?.data?.system_prompt === "string" ? node.data.system_prompt : ""}
                                sx={iconBtnSx}
                            />
                            <Tooltip title="Save version">
                                <IconButton size="small" sx={iconBtnSx} onClick={saveSystemPromptVersion}>
                                    <HugeiconsIcon icon={FloppyDiskIcon} size={15}/>
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Version history">
                                <IconButton size="small" sx={iconBtnSx} onClick={() => setShowPromptHistory(true)}>
                                    <HugeiconsIcon icon={Clock01Icon} size={15}/>
                                </IconButton>
                            </Tooltip>
                            <IconButton size="small" sx={iconBtnSx} onClick={() => setExpandPersona(true)}>
                                <HugeiconsIcon icon={Maximize01Icon} size={15}/>
                            </IconButton>
                        </Stack>
                    }
                />
                <TextField
                    value={typeof node?.data?.system_prompt === "string" ? node.data.system_prompt : ""}
                    size="small"
                    fullWidth
                    placeholder="Describe persona"
                    multiline
                    minRows={3}
                    maxRows={9}
                    sx={{mt: 0.75, ...fieldSx}}
                    onClick={() => setExpandPersona(true)}
                />
            </Box>

            <SystemPromptVariables node={node} setNode={setNode} onUpdate={onUpdate}/>

            <Box>
                <SubSectionHeader
                    title="User Input"
                    action={
                        <IconButton size="small" sx={iconBtnSx} onClick={() => setExpandUserInputMessage(true)}>
                            <HugeiconsIcon icon={Maximize01Icon} size={15}/>
                        </IconButton>
                    }
                />
                <TextField
                    value={typeof node?.data?.user_message === "string" ? node.data.user_message : ""}
                    size="small"
                    fullWidth
                    placeholder="Type your first message to start the AI conversation"
                    multiline
                    minRows={3}
                    maxRows={9}
                    sx={{mt: 0.75, ...fieldSx}}
                    onClick={() => setExpandUserInputMessage(true)}
                    helperText="This message will initiate the AI response and set the conversation context"
                />
            </Box>

            {[NodeType.GEN_AI_WORKFLOW, NodeType.CHATBOT, NodeType.AGENT].includes(node?.type) && (
                <>
                    <SubSectionHeader title="Parameter"/>
                    <FormControl size="small" fullWidth sx={fieldSx}>
                        <InputLabel id="llm-config-param-entity-label">Parameter Entity</InputLabel>
                        <Select
                            labelId="llm-config-param-entity-label"
                            value={node?.data?.parameter?.entity_type}
                            label="Parameter Entity"
                            onChange={(e) => {
                                const entity = project?.entities?.find(i => i?.name === e?.target?.value);
                                onChangeData("parameter", {
                                    entity_type: entity?.name || "",
                                    description: entity?.system_prompt || null,
                                    name: node?.data?.parameter?.name || "",
                                });
                            }}
                            onBlur={() => onUpdate(node)}
                        >
                            <MenuItem value=""><em>None</em></MenuItem>
                            {project?.entities?.map(entity => (
                                <MenuItem key={entity?.id} value={entity?.name}>{entity?.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        value={node?.data?.parameter?.name || ""}
                        size="small"
                        label="Parameter name"
                        placeholder="Enter the name of the parameter"
                        helperText="With this name you will be able to further access the variable."
                        sx={fieldSx}
                        onChange={(e) => onChangeData("parameter", {
                            entity_type: node?.data?.parameter?.entity_type || "",
                            name: e.target.value || "",
                        })}
                        onBlur={() => onUpdate(node)}
                    />
                </>
            )}
            <SubSectionHeader title="Structured Output"/>
            <FormControl size="small" fullWidth sx={fieldSx}>
                <InputLabel id="llm-config-structured-output-label">Structured output schema</InputLabel>
                <Select
                    labelId="llm-config-structured-output-label"
                    value={node?.data?.structured_output_object?.id || ""}
                    label="Structured output schema"
                    onChange={(e) => {
                        const entity = project?.entities?.find(i => i?.id === e.target.value);
                        onChangeData("structured_output_object", entity ? {
                            id: entity.id,
                            json_schema: entity.json_schema,
                            system_prompt: entity.system_prompt,
                        } : null);
                    }}
                    onBlur={() => onUpdate(node)}
                >
                    <MenuItem value=""><em>None</em></MenuItem>
                    {project?.entities
                        ?.filter(entity => (entity?.entity_type || entity?.entityType) === "JSONSchemaEntity")
                        .map(entity => (
                            <MenuItem key={entity?.id} value={entity?.id}>{entity?.name}</MenuItem>
                        ))}
                </Select>
                <FormHelperText>
                    Force the model to reply with JSON that follows the schema of the selected entity. Only JSON Schema entities are listed; leave empty for a free-text reply.
                </FormHelperText>
            </FormControl>


            <FormControl size="small" sx={fieldSx}>
                <InputLabel id="llm-config-topic-label">Topic</InputLabel>
                <Select
                    labelId="llm-config-topic-label"
                    label="Topic"
                    value={node?.data?.topic_id}
                    onBlur={() => onUpdate(node)}
                    onChange={e => {
                        const value = e.target.value;
                        setNode(prev => {
                            const state = {...prev};
                            const topic = activeTopics?.find(i => String(i?.id) === String(value));
                            state.data.topic_id = value === "" ? null : value;
                            state.data.topic_name = topic?.name || null;
                            state.data.slot_id = null;
                            return state;
                        });
                    }}
                >
                    <MenuItem value=""><em>None</em></MenuItem>
                    {isKbNode
                        ? activeTopics?.map(item => {
                            const isInactive = item?.server?.status && item.server.status !== "online";
                            return (
                                <MenuItem key={item?.id} value={item?.id}>
                                    <Stack direction="row" alignItems="center" gap={1} sx={{width: "100%"}}>
                                        {isInactive && (
                                            <Tooltip title={`Server status: ${item.server.status}`}>
                                                <Box sx={{
                                                    width: 8,
                                                    height: 8,
                                                    borderRadius: "50%",
                                                    bgcolor: "warning.main",
                                                    flexShrink: 0
                                                }}/>
                                            </Tooltip>
                                        )}
                                        <Typography fontSize={13} noWrap>{item?.name}</Typography>
                                        {isInactive && (
                                            <Typography fontSize={10} color="warning.main"
                                                        sx={{ml: "auto"}}>inactive</Typography>
                                        )}
                                    </Stack>
                                </MenuItem>
                            );
                        })
                        : selectedOrganization && activeTopics
                        ?.filter(item => item?.organization?.id === selectedOrganization || !item?.organization)
                        ?.map(item => (
                            <MenuItem key={item?.id} value={item?.id}>{item?.name}</MenuItem>
                        ))
                    }
                </Select>
            </FormControl>

            {node?.data?.topic_id && (
                <>
                    <SliderRow
                        label="Topic Score Threshold"
                        display={node?.data?.score_threshold || 0}
                        value={node?.data?.score_threshold || 0}
                        min={0} max={1} step={0.01}
                        onChange={e => onChangeData("score_threshold", e.target.value)}
                        onBlur={() => onUpdate(node)}
                    />
                    <SliderRow
                        label="Topic Results"
                        display={node?.data?.num_results || 0}
                        value={node?.data?.num_results || 0}
                        min={0} max={100} step={1}
                        onChange={e => onChangeData("num_results", e.target.value)}
                        onBlur={() => onUpdate(node)}
                    />
                </>
            )}

            <SubSectionHeader
                title="Function Tools"
                description="Enable the AI model to call external functions and retrieve dynamic data during conversations"
            />
            <Autocomplete
                size="small"
                options={allTools.filter(tool => !node?.data?.tools_obj?.some(obj => obj.name === tool?.name))}
                groupBy={(option) => option.serverName}
                getOptionLabel={option => option?.name || ""}
                renderOption={(props, option) => (
                    <Box component="li" {...props} sx={{display: "flex", alignItems: "center", gap: 1}}>
                        <Box sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            flexShrink: 0,
                            bgcolor: option.serverActive ? "success.main" : "warning.main"
                        }}/>
                        <Typography fontSize={13}>{option.name}</Typography>
                        {option.source === "custom" && (
                            <Typography fontSize={10} color="text.secondary" sx={{ml: "auto"}}>custom</Typography>
                        )}
                        {!option.serverActive && (
                            <Typography fontSize={10} color="warning.main" sx={{ml: "auto"}}>server
                                inactive</Typography>
                        )}
                    </Box>
                )}
                multiple={false}
                blurOnSelect
                clearOnBlur
                loading={mcpLoading}
                value={null}
                renderInput={(props) => <TextField {...props} size="small" label="Tools Object" sx={fieldSx}/>}
                onChange={async (e, newValue) => {
                    if (!newValue) return;
                    const updatedNode = {...node, data: {...node.data}};
                    const currentToolsObj = updatedNode.data.tools_obj || [];
                    if (newValue.source === "custom") {
                        updatedNode.data.tools_obj = [...currentToolsObj, {
                            name: newValue.name,
                            id: newValue.raw?.id,
                            flush: true,
                            source: "custom",
                        }];
                        onUpdate(updatedNode);
                    } else {
                        updatedNode.data.tools_obj = [...currentToolsObj, {
                            name: newValue.name,
                            server_id: newValue.serverId,
                            flush: true,
                            tool_details: newValue.raw,
                            source: "mcp",
                        }];
                        onUpdate(updatedNode);
                    }
                }}
            />
            {node?.data?.tools_obj?.length > 0 && (
                <Table size="small" sx={tableSx}>
                    <TableHead>
                        <TableRow sx={{backgroundColor: rgba(UI.accent, 0.04)}}>
                            <TableCell sx={thSx}>Tool</TableCell>
                            <TableCell align="center" sx={thSx}>Flush</TableCell>
                            <TableCell align="center" sx={{width: 32}}/>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {node?.data?.tools_obj?.map((tool, index) => {
                            const isCustom = tool.source === "custom" || !tool.server_id;
                            const server = !isCustom ? mcpServers?.find(s => s.id === tool.server_id) : null;
                            const matchedTool = !isCustom
                                ? allTools.find(t => t.name === tool.name && t.serverId === tool.server_id && t.source === "mcp")
                                : null;
                            const isMissing = !isCustom && mcpReady && !mcpLoading && !matchedTool;
                            const isInactive = !isCustom && !isMissing && ((server && !server.active) || matchedTool?.toolActive === false);

                            return (
                                <TableRow key={`${tool.name}-${index}`}>
                                    <TableCell>
                                        <Tooltip title={
                                                     isCustom
                                                         ? `Custom tool — ${tool.name}`
                                                         : isMissing
                                                             ? `${server?.name || "Unknown server"} — ${tool.name} · tool no longer exists`
                                                             : isInactive
                                                                 ? `${server?.name || "Unknown server"} — ${tool.name} · inactive`
                                                                 : `${server?.name || "Unknown server"} — ${tool.name}`
                                                 }>
                                            <Stack direction="row" alignItems="center" gap={0.5}>
                                                {!isCustom && (isMissing || isInactive) && (
                                                    <Box sx={{
                                                        width: 7,
                                                        height: 7,
                                                        borderRadius: "50%",
                                                        bgcolor: isMissing ? "error.main" : "warning.main",
                                                        flexShrink: 0
                                                    }}/>
                                                )}
                                                <Typography noWrap fontSize={12} sx={{
                                                    maxWidth: "160px",
                                                    color: UI.ink
                                                }}>{tool.name}</Typography>
                                            </Stack>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Checkbox
                                            checked={tool.flush}
                                            size="small"
                                            sx={{
                                                p: 0.25,
                                                color: rgba(UI.ink, 0.35),
                                                "&.Mui-checked": {color: UI.accent}
                                            }}
                                            onChange={(e) => {
                                                const updatedNode = {...node};
                                                const updatedToolsObj = [...updatedNode.data.tools_obj];
                                                updatedToolsObj[index] = {...tool, flush: e.target.checked};
                                                updatedNode.data.tools_obj = updatedToolsObj;
                                                onUpdate(updatedNode);
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell align="center">
                                        <IconButton
                                            size="small"
                                            sx={{...iconBtnDangerSx, p: 0.25}}
                                            onClick={() => {
                                                const updatedNode = {...node};
                                                updatedNode.data.tools_obj = updatedNode.data.tools_obj.filter((_, i) => i !== index);
                                                onUpdate(updatedNode);
                                            }}
                                        >
                                            <HugeiconsIcon icon={Cancel01Icon} size={13}/>
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            )}

            <SubSectionHeader title="Skills" description="Attach reusable prompt templates to this node"/>
            <Autocomplete
                size="small"
                options={(project?.skills || []).filter(skill => !node?.data?.skills?.some(s => s.id === skill.id))}
                getOptionLabel={option => option?.name || ""}
                renderOption={(props, option) => (
                    <Box component="li" {...props}>
                        <Stack>
                            <Typography fontSize={13}>{option.name}</Typography>
                            {option.description && (
                                <Typography fontSize={10} color={UI.muted} noWrap
                                            sx={{maxWidth: 240}}>{option.description}</Typography>
                            )}
                        </Stack>
                    </Box>
                )}
                multiple={false}
                blurOnSelect
                clearOnBlur
                value={null}
                renderInput={(props) => <TextField {...props} size="small" label="Skills" sx={fieldSx}/>}
                onChange={(e, newValue) => {
                    if (!newValue) return;
                    const updatedNode = {...node, data: {...node.data}};
                    const current = updatedNode.data.skills || [];
                    updatedNode.data.skills = [...current, {
                        id: newValue.id,
                        name: newValue.name,
                        description: newValue.description || "",
                        content: newValue.content || "",
                        tools: newValue.tools || [],
                    }];
                    setNode(updatedNode);
                    onUpdate(updatedNode);
                }}
            />
            {node?.data?.skills?.length > 0 && (
                <Table size="small" sx={tableSx}>
                    <TableHead>
                        <TableRow sx={{backgroundColor: rgba(UI.accent, 0.04)}}>
                            <TableCell sx={thSx}>Skill</TableCell>
                            <TableCell sx={thSx}>Tools</TableCell>
                            <TableCell align="center" sx={{width: 32}}/>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {node?.data?.skills?.map((skill, index) => {
                            const stillExists = project?.skills?.some(ps => ps.id === skill.id);
                            return (
                                <TableRow key={`${skill.id}-${index}`}>
                                    <TableCell>
                                        <Tooltip
                                            title={!stillExists ? `${skill.name} · skill no longer exists` : skill.description || skill.name}>
                                            <Stack direction="row" alignItems="center" gap={0.5}>
                                                {!stillExists && (
                                                    <Box sx={{
                                                        width: 7,
                                                        height: 7,
                                                        borderRadius: "50%",
                                                        bgcolor: "error.main",
                                                        flexShrink: 0
                                                    }}/>
                                                )}
                                                <Typography noWrap fontSize={12} sx={{
                                                    maxWidth: "160px",
                                                    color: UI.ink
                                                }}>{skill.name}</Typography>
                                            </Stack>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell>
                                        <Typography fontSize={11} color={UI.muted}>
                                            {skill.tools?.length > 0 ? `${skill.tools.length} tool${skill.tools.length > 1 ? "s" : ""}` : "—"}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                        <IconButton
                                            size="small"
                                            sx={{...iconBtnDangerSx, p: 0.25}}
                                            onClick={() => {
                                                const updatedNode = {...node, data: {...node.data}};
                                                updatedNode.data.skills = updatedNode.data.skills.filter((_, i) => i !== index);
                                                setNode(updatedNode);
                                                onUpdate(updatedNode);
                                            }}
                                        >
                                            <HugeiconsIcon icon={Cancel01Icon} size={13}/>
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            )}

            <SettingsCustomParams node={node} setNode={setNode} onUpdate={onUpdate}
                                  hiddenKeys={[REASONING_EFFORT_KEY]}/>
        </>
    );
};

const SystemPromptVariables = ({node, setNode, onUpdate}) => {
    const vars = node?.data?.system_prompt_variables || [];

    return (
        <Stack gap={1}>
            <SubSectionHeader
                title="System Prompt Variables"
                action={
                    <Tooltip title="Add variable" disableInteractive>
                        <IconButton
                            size="small"
                            sx={iconBtnSx}
                            onClick={() => {
                                setNode(prev => {
                                    const state = {...prev};
                                    if (!state?.data?.system_prompt_variables) {
                                        state.data.system_prompt_variables = [];
                                    }
                                    state.data.system_prompt_variables.push("");
                                    return state;
                                });
                            }}
                        >
                            <HugeiconsIcon icon={Add01Icon} size={16}/>
                        </IconButton>
                    </Tooltip>
                }
            />
            {vars.length === 0 && (
                <Typography sx={{fontSize: 11.5, color: rgba(UI.muted, 0.8)}}>Please add variables</Typography>
            )}
            {vars.map((variable, index) => (
                <Stack direction="row" gap={0.75} alignItems="center" key={variable}>
                    <TextField
                        size="small"
                        defaultValue={variable}
                        InputLabelProps={{shrink: true}}
                        placeholder="Enter variable name"
                        sx={{flex: 1, ...fieldSx}}
                        onBlur={(e) => {
                            setNode(prev => {
                                const state = {...prev};
                                if (!state?.data?.system_prompt_variables) {
                                    state.data.system_prompt_variables = [];
                                }
                                state.data.system_prompt_variables[index] = e.target.value || "";
                                onUpdate(state);
                                return state;
                            });
                        }}
                    />
                    <IconButton
                        size="small"
                        sx={iconBtnDangerSx}
                        onClick={() => {
                            setNode(prev => {
                                const state = {...prev};
                                state.data.system_prompt_variables = state?.data?.system_prompt_variables?.filter((i, ind) => ind !== index);
                                return state;
                            });
                        }}
                    >
                        <HugeiconsIcon icon={Cancel01Icon} size={15}/>
                    </IconButton>
                </Stack>
            ))}
        </Stack>
    );
};
